import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { BLACKOUT_SCOPES } from "helpers/blackouts"

/**
 * A day in the feed is expected to carry `blackouts`, but the endpoint is not built yet and the
 * sibling blackout endpoints have already shipped shapes the contract did not describe. Reading
 * through this keeps a malformed day from white-screening every calendar view.
 */
const toBlackouts = (day) => (Array.isArray(day?.blackouts) ? day.blackouts : [])

/**
 * Reads the day-resolved blackout feed (`getCalendarBlackouts`) and answers the two
 * questions every calendar surface asks: "is this day blacked out?" and "is this booking
 * in conflict?".
 *
 * The booking side is derived on the client because calendar bookings carry `experience_name`
 * but no experience id, so a booking can only be matched to a blackout by listing *title*. That
 * is ambiguous: two listings sharing a title make one of them report a conflict it does not have.
 * Fixing it needs the backend — either `blackout_conflict` per booking (preferred, and already
 * read first by the reservation detail page) or an experience id on each calendar booking.
 */
export const useCalendarBlackouts = () => {
  const days = useSelector((state) => state.blackouts?.calendar?.days || {})
  const isLoading = useSelector((state) => Boolean(state.blackouts?.calendar?.loading))

  const getDay = useCallback((dateKey) => days[dateKey] || null, [days])

  const isBlackedOut = useCallback((dateKey) => Boolean(days[dateKey]), [days])

  /**
   * The affected listing titles per day, used to match bookings that carry an experience
   * name but no id. Built once so a month of day cards does not rebuild it per cell.
   */
  const listingTitlesByDay = useMemo(() => {
    return Object.entries(days).reduce((acc, [dateKey, day]) => {
      const titles = toBlackouts(day).flatMap((blackout) => (blackout.listings || []).map((listing) => listing.title))
      acc[dateKey] = new Set(titles)
      return acc
    }, {})
  }, [days])

  /**
   * The blackout a booking conflicts with, or null.
   *
   * A day-wide business closure catches every booking on the day. Any other scope only
   * catches bookings on one of the listings it affects.
   */
  const getBookingConflict = useCallback(
    (booking) => {
      const dateKey = booking?.date
      const day = dateKey && days[dateKey]
      if (!day) return null

      const blackouts = toBlackouts(day)
      const businessBlackout = blackouts.find(
        (blackout) => blackout.scope === BLACKOUT_SCOPES.BUSINESS && !blackout.start_time && !blackout.end_time
      )
      if (businessBlackout) return businessBlackout

      const titles = listingTitlesByDay[dateKey]
      const bookingTitle = booking?.experience_name || booking?.title
      if (!titles?.size || !bookingTitle || !titles.has(bookingTitle)) return null

      return blackouts.find((blackout) => (blackout.listings || []).some((listing) => listing.title === bookingTitle)) || null
    },
    [days, listingTitlesByDay]
  )

  return { days, isLoading, getDay, isBlackedOut, getBookingConflict }
}

export default useCalendarBlackouts
